import { useContext, useCallback } from 'react';
import { DecisionVoucherContext } from './DecisionVoucherContext';
import { UseDecisionVoucherReturn } from '../hooks/useDecisionVoucher';
import {
  DecisionVoucherV2Step,
  IDecisionVoucherV2,
  IAgreementConfigurationV2,
  IRefusalConfigurationV2,
  ISendingConfigurationV2,
} from '../models/decisionVoucherV2';

/**
 * Hook pour utiliser le contexte DecisionVoucher
 */
export function useDecisionVoucherContext(): UseDecisionVoucherReturn {
  const context = useContext(DecisionVoucherContext);
  if (!context) {
    throw new Error('useDecisionVoucherContext doit être utilisé à l\'intérieur d\'un DecisionVoucherProvider');
  }
  return context;
}

/**
 * Hook pour accéder uniquement aux données de qualification
 */
export function useDecisionVoucherQualification() {
  const { voucher, updateVoucher } = useDecisionVoucherContext();

  const updateQualification = useCallback((updates: Partial<IDecisionVoucherV2['qualification']>) => {
    if (!voucher) return;

    updateVoucher({
      qualification: {
        ...voucher.qualification,
        ...updates
      }
    });
  }, [voucher, updateVoucher]);

  return {
    qualificationData: voucher?.qualification,
    updateQualification,
  };
}

/**
 * Hook pour accéder uniquement aux données de configuration (accord)
 */
export function useDecisionVoucherAgreementConfig() {
  const { voucher, updateVoucher } = useDecisionVoucherContext();

  const updateAgreementConfig = useCallback((updates: Partial<IAgreementConfigurationV2>) => {
    if (!voucher) return;

    // Si la configuration n'existe pas encore, créer une configuration initiale
    const baseConfig: IAgreementConfigurationV2 = voucher.agreementConfiguration || {
      amountDetails: [],
      totalAmountTTC: 0,
      totalAmountHT: 0,
      totalAmountSeller: 0,
      totalAmountSellerCommercial: 0,
      totalAmountCustomer: 0,
      comment: "",
    };

    updateVoucher({
      agreementConfiguration: {
        ...baseConfig,
        ...updates
      }
    });
  }, [voucher, updateVoucher]);

  return {
    agreementConfig: voucher?.agreementConfiguration,
    updateAgreementConfig,
  };
}

/**
 * Hook pour accéder uniquement aux données de configuration (refus)
 */
export function useDecisionVoucherRefusalConfig() {
  const { voucher, updateVoucher } = useDecisionVoucherContext();

  const updateRefusalConfig = useCallback((updates: Partial<IRefusalConfigurationV2>) => {
    if (!voucher) return;

    // Si la configuration n'existe pas encore, créer une configuration initiale
    const baseConfig: IRefusalConfigurationV2 = voucher.refusalConfiguration || {
      reasonType: null,
      reasonDetails: "",
    };

    updateVoucher({
      refusalConfiguration: {
        ...baseConfig,
        ...updates
      }
    });
  }, [voucher, updateVoucher]);

  return {
    refusalConfig: voucher?.refusalConfiguration,
    updateRefusalConfig,
  };
}

/**
 * Hook pour accéder uniquement aux données d'envoi
 */
export function useDecisionVoucherSendingConfig() {
  const { voucher, updateVoucher } = useDecisionVoucherContext();

  const updateSendingConfig = useCallback((updates: Partial<ISendingConfigurationV2>) => {
    if (!voucher) return;

    // Si la configuration n'existe pas encore, créer une configuration initiale
    const baseConfig: ISendingConfigurationV2 = voucher.sendingConfiguration || {
      signatureMethod: null,
      recipients: [],
    };

    updateVoucher({
      sendingConfiguration: {
        ...baseConfig,
        ...updates
      }
    });
  }, [voucher, updateVoucher]);

  return {
    sendingConfig: voucher?.sendingConfiguration,
    updateSendingConfig,
  };
}

/**
 * Hook pour la navigation entre les étapes
 */
export function useDecisionVoucherNavigation() {
  const {
    voucher,
    goToNextStep,
    goToPreviousStep,
    setCurrentStep
  } = useDecisionVoucherContext();

  const canGoNext = useCallback(() => {
    // Logic pour déterminer si on peut passer à l'étape suivante
    if (!voucher) return false;

    switch (voucher.step) {
      case DecisionVoucherV2Step.QUALIFICATION:
        // Vérifier que les données de qualification sont complètes
        return voucher.qualification.isContractValid !== null &&
               voucher.qualification.isKilometrageLimitRespected !== null &&
               voucher.qualification.isDeclarationTimeRespected !== null &&
               voucher.qualification.arePartsEligible !== null &&
               voucher.qualification.finalDecision !== undefined;

      case DecisionVoucherV2Step.CONFIGURATION:
        // Vérifier que la configuration est complète selon le type de décision
        return true; // À implémenter selon les besoins

      case DecisionVoucherV2Step.VALIDATION:
        // Toujours à la fin
        return false;

      default:
        return false;
    }
  }, [voucher]);

  const canGoPrevious = useCallback(() => {
    // Logic pour déterminer si on peut revenir à l'étape précédente
    return voucher?.step !== DecisionVoucherV2Step.QUALIFICATION;
  }, [voucher]);

  return {
    currentStep: voucher?.step,
    goToNextStep,
    goToPreviousStep,
    setCurrentStep,
    canGoNext,
    canGoPrevious,
  };
}

/**
 * Alias de useDecisionVoucherNavigation pour compatibilité
 */
export function useStepNavigation() {
  const navigation = useDecisionVoucherNavigation();
  const { isLoading } = useDecisionVoucherContext();

  return {
    ...navigation,
    isLoading,
  };
}

/**
 * Hook pour les actions globales du bon
 */
export function useDecisionVoucherActions() {
  const {
    voucher,
    saveVoucher,
    createNewVersion,
    switchToVersion,
    voucherHistory,
    loadHistory,
    isDirty,
    isSaving,
  } = useDecisionVoucherContext();

  // Fonction pour finaliser le bon
  const finalizeVoucher = useCallback(async () => {
    if (!voucher) return null;

    // Mettre à jour le statut et sauvegarder
    // const updatedVoucher = {
    //   ...voucher,
    //   status: 'FINAL'
    // };

    // Cette implémentation est temporaire, à adapter selon les besoins réels
    return saveVoucher();
  }, [voucher, saveVoucher]);

  return {
    isDirty,
    isSaving,
    saveVoucher,
    finalizeVoucher,
    createNewVersion,
    switchToVersion,
    voucherHistory,
    loadHistory,
  };
}